'use client'

import React, { useState, useEffect, useRef } from 'react'
import { AnimatedSpreadButton } from '../Common/AnimatedSpreadButton'
import { ButtonWithHoverReveal } from '../Common/ButtonWithHoverReveal'
import { useLanguage } from '@/app/(frontend)/providers/LanguageProvider'
import { ChevronDown } from 'lucide-react'

const ContactForm = () => {
  const { language } = useLanguage()
  const isArabic = language === 'ar'

  const [file, setFile] = useState<File | null>(null)
  const [services, setServices] = useState<
    { id: string; title: string | { en?: string; ar?: string } }[]
  >([])

  const [selected, setSelected] = useState('')
  const [open, setOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const dropdownRef = useRef<HTMLDivElement>(null)

  // ✅ New states for message handling
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [statusType, setStatusType] = useState<'success' | 'error' | null>(null)

  const getServiceTitle = (service: any) => {
    if (typeof service.title === 'string') return service.title
    return isArabic ? service.title?.ar : service.title?.en
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const locale = isArabic ? 'ar' : 'en'

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_SERVER_URL}/api/services?locale=${locale}&sort=createdAt`,
        )
        const data = await res.json()
        if (Array.isArray(data?.docs)) {
          setServices(data.docs)
        }
      } catch (error) {
        console.error('Failed to fetch services:', error)
      }
    }

    fetchServices()
  }, [locale])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null
    setFile(selectedFile)
  }

  const handleClick = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)

    const form = e.currentTarget
    const formData = new FormData(form)

    formData.append('service', selected)

    try {
      const res = await fetch(`/api/submit-contact-form`, {
        method: 'POST',
        body: formData,
      })

      if (!res.ok) throw new Error('Failed to send enquiry')

      setStatusType('success')
      setStatusMessage(isArabic ? 'تم إرسال النموذج بنجاح!' : 'Form submitted successfully!')
      form.reset()
      setFile(null)
      setSelected('')
    } catch (error) {
      console.error(error)
      setStatusType('error')
      setStatusMessage(isArabic ? 'فشل إرسال النموذج' : 'Failed to submit form')
    } finally {
      setIsSubmitting(false)
    }
  }

  // ✅ Clear message after 5s
  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => {
        setStatusMessage(null)
        setStatusType(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [statusMessage])

  return (
    <form
      className="w-full xl:w-[739px] 2xl:w-[900px] h-full space-y-[24px] flex flex-col"
      onSubmit={handleClick}
    >
      <h2 className="text-[#414141] text-[24px] md:text-[30px] lg:text-[40px] font-[500] xl:mb-[40px]">
        {isArabic ? 'اتصل بنا' : 'Contact Us'}
      </h2>

      <div className="w-full xl:h-[505px] flex flex-col gap-[36px]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px]">
          <input
            type="text"
            name="botCheck"
            autoComplete="new-password"
            className="hidden"
            tabIndex={-1}
          />
          <div>
            <label className="block text-[15px] md:text-[12px] text-[#414141] mb-1">
              {isArabic ? 'الاسم الكامل' : 'Full Name'}
            </label>
            <input
              type="text"
              name="name"
              placeholder={isArabic ? 'الاسم الكامل' : 'Full Name'}
              required
              className="w-full h-[44px] bg-[#f5f5f5] border-b border-[#414141] px-3 text-sm outline-none"
              minLength={2}
              maxLength={100}
              pattern="^[A-Za-z\s]{2,100}$"
              title="Only alphabets are allowed"
            />
          </div>
          <div>
            <label className="block text-[15px] md:text-[12px] text-[#414141] mb-1">
              {isArabic ? 'البريد الإلكتروني' : 'E-Mail'}
            </label>
            <input
              type="email"
              name="email"
              placeholder={isArabic ? 'البريد الإلكتروني' : 'E-Mail Address'}
              required
              className="w-full h-[44px] bg-[#f5f5f5] border-b border-[#414141] px-3 text-sm outline-none"
              maxLength={120}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px]">
          <div>
            <label className="block text-[15px] md:text-[12px] text-[#414141] mb-1">
              {isArabic ? 'رقم الاتصال' : 'Contact Number'}
            </label>
            <input
              type="text"
              name="phone"
              placeholder={isArabic ? 'رقم الاتصال' : 'Contact Number'}
              required
              className="w-full h-[44px] bg-[#f5f5f5] border-b border-[#414141] px-3 text-sm outline-none"
              maxLength={15}
              pattern="^[0-9]{7,15}$"
              title="Enter a valid phone number (digits only, 7–15 digits)"
              onInput={(e) => {
                const input = e.currentTarget
                input.value = input.value.replace(/\D/g, '')
              }}
            />
          </div>
          <div>
            <label className="block text-[15px] md:text-[12px] text-[#414141] mb-1">
              {isArabic ? 'مجال العمل' : 'Industry'}
            </label>
            <input
              type="text"
              name="industry"
              placeholder={isArabic ? 'مجال العمل' : 'Industry'}
              required
              className="w-full h-[44px] bg-[#f5f5f5] border-b border-[#414141] px-3 text-sm outline-none"
              minLength={2}
              maxLength={80}
            />
          </div>
        </div>

        <div>
          <label className="block text-[15px] md:text-[12px] text-[#414141] mb-1">
            {isArabic ? 'اختر الخدمة' : 'Select Service'}
          </label>

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              className={`w-full h-[44px] bg-[#f5f5f5] border-b border-[#414141] text-[13px] md:text-sm px-3 appearance-none outline-none ${
                isArabic ? 'text-right' : 'text-left'
              }`}
              onClick={() => setOpen(!open)}
            >
              {selected
                ? getServiceTitle(services.find((s) => s.id === selected))
                : isArabic
                  ? 'اختر الخدمة'
                  : 'Select Service'}
            </button>

            <ChevronDown
              strokeWidth={1}
              className={`absolute top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 ${
                isArabic ? 'left-3' : 'right-3'
              }`}
            />

            {open && (
              <ul className="absolute z-10 w-full bg-[#f5f5f5] border border-gray-300 max-h-60 overflow-y-auto shadow-md">
                {services.map((service, i) => (
                  <li
                    key={service.id}
                    className={`px-3 py-2 text-[12px] md:text-sm text-gray-700 ${
                      services.length - 1 === i ? '' : 'border-b border-gray-300'
                    } hover:bg-gray-200 cursor-pointer`}
                    onClick={() => {
                      setSelected(service.id)
                      setOpen(false)
                    }}
                  >
                    {getServiceTitle(service)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div>
          <label className="block text-[15px] md:text-[12px] text-[#414141] mb-1">
            {isArabic ? 'تحميل صورة (اختياري)' : 'Upload Image (Optional)'}
          </label>
          <div className="relative w-full h-[44px] bg-[#f5f5f5] border-b border-[#414141] px-3 text-sm flex items-center cursor-pointer">
            <span className="text-[#414141] truncate">
              {file?.name || (isArabic ? 'اختر صورة' : 'Choose Image')}
            </span>
            <input
              type="file"
              name="file"
              accept="image/*"
              onChange={handleFileChange}
              className="absolute inset-0 opacity-0 cursor-pointer"
            />
          </div>
        </div>

        <div>
          <label className="block text-[15px] md:text-[12px] text-[#414141] mb-1">
            {isArabic ? 'أرسل رسالة' : 'Send a Message'}
          </label>
          <textarea
            name="message"
            rows={6}
            required
            placeholder={isArabic ? 'الرسالة' : 'Message'}
            className="w-full bg-[#f5f5f5] border-b border-[#414141] px-3 pt-2 text-sm outline-none resize-none"
            maxLength={1000}
          />
        </div>

        <div className="xl:w-[237px]">
          <button
            disabled={isSubmitting}
            type="submit"
            className="opacity-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ButtonWithHoverReveal
              disabled={isSubmitting}
              text={
                isSubmitting
                  ? isArabic
                    ? 'جارٍ الإرسال...'
                    : 'Submitting...'
                  : isArabic
                    ? 'إرسال الاستفسار'
                    : 'Submit Enquiry'
              }
            />
          </button>
        </div>

        {/* ✅ Inline status message (below button) */}
        {statusMessage && (
          <p
            className={`text-sm font-medium mt-2 ${
              statusType === 'success' ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {statusMessage}
          </p>
        )}
      </div>
    </form>
  )
}

export default ContactForm
